import React, { useEffect, useState, useMemo } from 'react'
import { Container, EzosLoader } from '../../components'
import { useParams } from 'react-router-dom'
import {
    useCompany,
    groupVendorPricingByService,
    getAreaPricingForService,
    getUnitPricingForService,
} from '../../hooks/useCompany'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import Tooltip from '@mui/material/Tooltip'
import { AreaConfig, UnitConfig, VendorPricingLink } from '../../models'
import { axiosInstance } from '../../helpers'

// Add decimal validation helper
const isValidDecimal = (value: string) => {
    return value === '' || /^\d*(\.\d{0,2})?$/.test(value)
}

export const UpdateVendorPricingForm = () => {
    const theme = useTheme()
    const { link_token } = useParams<{ link_token: string }>()

    const [loading, setLoading] = useState(true)

    const [
        vendorPricingLink,
        setVendorPricingLink,
    ] = useState<VendorPricingLink | null>(null)
    const [unitConfigs, setUnitConfigs] = useState<UnitConfig[]>([])
    const [areaConfigs, setAreaConfigs] = useState<AreaConfig[]>([])
    const {
        fetchVendorPricing,
        vendorPricing,
        updateVendorPricing,
        isSubmitted,
    } = useCompany()

    useEffect(() => {
        setLoading(true)

        const areaConfigPromise = axiosInstance.get(
            'apt_config/vendor-pricing-area-config/',
            {
                params: {
                    shows_on_schedule: true,
                    link_token: link_token,
                },
            },
        )
        const unitConfigPromise = axiosInstance.get(
            'apt_config/vendor-pricing-unit-config/',
            {
                params: {
                    shows_on_schedule: true,
                    link_token: link_token,
                },
            },
        )
        const vendorPricingPromise = fetchVendorPricing(link_token)

        const vendorPricingLinkPromise = axiosInstance.get(
            'vendor-pricing-link/',
            {
                params: {
                    link_token: link_token,
                },
            },
        )

        Promise.all([
            areaConfigPromise,
            unitConfigPromise,
            vendorPricingPromise,
            vendorPricingLinkPromise,
        ])
            .then((values) => {
                setAreaConfigs(values[0].data)
                setUnitConfigs(values[1].data)
                setVendorPricingLink(values[2].data)
                setLoading(false)
            })
            .catch((e) => {
                console.log(e)
                setLoading(false)
            })
    }, [])

    const grouped = useMemo(
        () =>
            Array.isArray(vendorPricing)
                ? groupVendorPricingByService(vendorPricing)
                : {},
        [vendorPricing],
    )
    const serviceNames = useMemo(() => Object.keys(grouped), [grouped])

    // Map service ID to pricing array
    const groupedById = useMemo(() => {
        const result: Record<number, any[]> = {}
        Object.values(grouped).forEach((arr) => {
            if (arr[0]?.service) {
                result[arr[0].service] = arr
            }
        })
        return result
    }, [grouped])

    const serviceIdList = useMemo(
        () => Object.values(groupedById).map((list) => list[0]?.service),
        [groupedById],
    )
    const [selectedService, setSelectedService] = useState<number | null>(
        serviceIdList[0] || null,
    )

    // State to track edits per service
    const [editedPricing, setEditedPricing] = useState<Record<number, any[]>>(
        {},
    )
    // State to track completion per service
    const [completedServices, setCompletedServices] = useState<
        Record<number, boolean>
    >({})
    // State to track which services have been reviewed (visited at least once)
    const [reviewedServices, setReviewedServices] = useState<
        Record<number, boolean>
    >({})

    useEffect(() => {
        if (!selectedService && serviceIdList.length > 0) {
            setSelectedService(serviceIdList[0] || null)
        } else if (
            selectedService &&
            !serviceIdList.includes(selectedService)
        ) {
            setSelectedService(serviceIdList[0] || null)
        }
    }, [serviceIdList, selectedService])

    useEffect(() => {
        if (
            !vendorPricing ||
            !Array.isArray(vendorPricing) ||
            unitConfigs.length === 0 ||
            areaConfigs.length === 0
        ) {
            return
        }

        // Get all unique service IDs from vendorPricing
        const allServiceIds = Array.from(
            new Set(vendorPricing.map((vp: any) => vp.service)),
        )

        // Build full array for all services
        const allPricingByService: Record<number, any[]> = {}

        allServiceIds.forEach((serviceId) => {
            // Area configs
            const areaPricing = areaConfigs.map((areaConfig) => {
                const found = vendorPricing.find(
                    (item: any) =>
                        item.area_config?.id === areaConfig.id &&
                        item.service === serviceId &&
                        !item.unit_config,
                )
                return (
                    found || {
                        id: `area-${serviceId}-${areaConfig.id}`,
                        service: serviceId,
                        area_config: areaConfig,
                        unit_config: null,
                        price: null,
                    }
                )
            })

            // Unit configs
            const unitPricing = unitConfigs.map((unitConfig) => {
                const found = vendorPricing.find(
                    (item: any) =>
                        item.unit_config?.id === unitConfig.id &&
                        item.service === serviceId &&
                        !item.area_config,
                )
                return (
                    found || {
                        id: `unit-${serviceId}-${unitConfig.id}`,
                        service: serviceId,
                        area_config: null,
                        unit_config: unitConfig,
                        price: null,
                    }
                )
            })

            allPricingByService[serviceId] = [...areaPricing, ...unitPricing]
        })

        setEditedPricing(allPricingByService)
    }, [vendorPricing, unitConfigs, areaConfigs])

    // Mark service as reviewed when selected
    useEffect(() => {
        if (selectedService) {
            setReviewedServices((prev) => ({
                ...prev,
                [selectedService]: true,
            }))
        }
    }, [selectedService])

    if (loading) {
        return (
            <Container
                style={{
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <EzosLoader />
            </Container>
        )
    }

    if (isSubmitted) {
        return (
            <Container
                style={{
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Typography>Pricing submitted successfully</Typography>
            </Container>
        )
    }

    const currentPricing = selectedService
        ? groupedById[selectedService] || []
        : []
    const areaPricing = getAreaPricingForService(currentPricing)
    const unitPricing = getUnitPricingForService(currentPricing)

    // Helper to build full area pricing list for the selected service
    const buildFullAreaPricing = (
        areaConfigs: AreaConfig[],
        currentPricing: any[],
        serviceId: number | null,
    ) => {
        if (!serviceId) return []
        return areaConfigs.map((areaConfig) => {
            const found = currentPricing.find(
                (item) =>
                    item.area_config?.id === areaConfig.id &&
                    item.service === serviceId &&
                    !item.unit_config,
            )
            return (
                found || {
                    id: `area-${serviceId}-${areaConfig.id}`,
                    service: serviceId,
                    area_config: areaConfig,
                    unit_config: null,
                    price: '',
                }
            )
        })
    }

    // Helper to build full unit pricing list for the selected service
    const buildFullUnitPricing = (
        unitConfigs: UnitConfig[],
        currentPricing: any[],
        serviceId: number | null,
    ) => {
        if (!serviceId) return []
        return unitConfigs.map((unitConfig) => {
            const found = currentPricing.find(
                (item) =>
                    item.unit_config?.id === unitConfig.id &&
                    item.service === serviceId &&
                    !item.area_config,
            )
            return (
                found || {
                    id: `unit-${serviceId}-${unitConfig.id}`,
                    service: serviceId,
                    area_config: null,
                    unit_config: unitConfig,
                    price: '',
                }
            )
        })
    }

    // Helper to get the editable pricing for the current service
    const editableAreaPricing =
        selectedService && editedPricing[selectedService]
            ? buildFullAreaPricing(
                  areaConfigs,
                  editedPricing[selectedService],
                  selectedService,
              )
            : buildFullAreaPricing(areaConfigs, areaPricing, selectedService)
    const editableUnitPricing =
        selectedService && editedPricing[selectedService]
            ? buildFullUnitPricing(
                  unitConfigs,
                  editedPricing[selectedService],
                  selectedService,
              )
            : buildFullUnitPricing(unitConfigs, unitPricing, selectedService)

    // Handle price change for area pricing
    const handleAreaPriceChange = (id: string | number, value: string) => {
        if (!selectedService) return
        if (!isValidDecimal(value)) return
        const newValue = value === '' ? null : value
        const updatedArea = editableAreaPricing.map((item) =>
            item.id === id ? { ...item, price: newValue } : item,
        )
        // Merge with current unit pricing for this service
        const currentUnits = editedPricing[selectedService]
            ? editedPricing[selectedService].filter((item) => item.unit_config)
            : editableUnitPricing
        setEditedPricing((prev) => ({
            ...prev,
            [selectedService]: [...updatedArea, ...currentUnits],
        }))
    }

    // Handle price change for unit pricing
    const handleUnitPriceChange = (id: string | number, value: string) => {
        if (!selectedService) return
        if (!isValidDecimal(value)) return
        const newValue = value === '' ? null : value
        const updatedUnits = editableUnitPricing.map((item) =>
            item.id === id ? { ...item, price: newValue } : item,
        )
        // Merge with current area pricing for this service
        const currentAreas = editedPricing[selectedService]
            ? editedPricing[selectedService].filter((item) => item.area_config)
            : editableAreaPricing
        setEditedPricing((prev) => ({
            ...prev,
            [selectedService]: [...currentAreas, ...updatedUnits],
        }))
    }

    // Handle submit for all services
    const handleSubmitAll = async () => {
        // Gather all pricing data (edited or original) for each service and flatten into a single array
        const allPricing = serviceIdList.flatMap((serviceId) => {
            return editedPricing[serviceId] || groupedById[serviceId] || []
        })
        updateVendorPricing({
            body: {
                link_token: link_token,
                pricing: allPricing,
            },
        })
        // Mark all as completed
        setCompletedServices((prev) => {
            const updated: Record<number, boolean> = { ...prev }
            serviceIdList.forEach((id) => {
                updated[id] = true
            })
            return updated
        })
    }

    // Handle marking current service as done and moving to next
    const handleDoneService = () => {
        if (!selectedService) return
        setCompletedServices((prev) => ({ ...prev, [selectedService]: true }))
        // Move to next incomplete service, or stay if all are done
        const currentIdx = serviceIdList.indexOf(selectedService)
        const nextIdx = serviceIdList.findIndex(
            (id, idx) => idx > currentIdx && !completedServices[id],
        )
        if (nextIdx !== -1) {
            setSelectedService(serviceIdList[nextIdx])
        } else {
            // If no next incomplete, try previous incomplete
            const prevIdx = serviceIdList.findIndex(
                (id, idx) => idx < currentIdx && !completedServices[id],
            )
            if (prevIdx !== -1) {
                setSelectedService(serviceIdList[prevIdx])
            }
        }
    }

    // Check if all services have been reviewed
    const allReviewed = serviceIdList.every((id) => reviewedServices[id])

    // Get apartment and vendor name from the first item
    const apartmentName = vendorPricingLink?.apartment_name || ''
    const vendorName = vendorPricingLink?.vendor_name || ''

    return (
        <Container>
            <Box display="flex" flexDirection="column" minHeight={400}>
                {/* Enhanced Header at the very top */}
                <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                    mb={4}
                    p={3}
                    borderRadius={2}
                    boxShadow={2}
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        minHeight: 80,
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ letterSpacing: 0.5 }}
                    >
                        {apartmentName}
                    </Typography>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ letterSpacing: 0.5 }}
                    >
                        {vendorName}
                    </Typography>
                </Box>
                {/* Main flex row below header */}
                <Box display="flex" flex={1}>
                    {/* Left panel: Services */}
                    <Box
                        minWidth={220}
                        borderRight={1}
                        borderColor="divider"
                        p={2}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography fontWeight="bold" mb={2}>
                                Services
                            </Typography>
                            <List>
                                {serviceIdList.map((serviceId) => {
                                    const arr = groupedById[serviceId]
                                    const serviceName =
                                        arr && arr[0]?.service_name
                                    return (
                                        <ListItem
                                            key={serviceId}
                                            disablePadding
                                        >
                                            <ListItemButton
                                                selected={
                                                    selectedService ===
                                                    serviceId
                                                }
                                                onClick={() =>
                                                    setSelectedService(
                                                        serviceId,
                                                    )
                                                }
                                            >
                                                <Typography
                                                    fontWeight={
                                                        selectedService ===
                                                        serviceId
                                                            ? 'bold'
                                                            : undefined
                                                    }
                                                    color={
                                                        selectedService ===
                                                        serviceId
                                                            ? 'primary'
                                                            : undefined
                                                    }
                                                    sx={{ flex: 1 }}
                                                >
                                                    {serviceName}
                                                </Typography>
                                                {completedServices[
                                                    serviceId
                                                ] ? (
                                                    <CheckCircleIcon
                                                        color="primary"
                                                        fontSize="small"
                                                    />
                                                ) : (
                                                    <RadioButtonUncheckedIcon
                                                        color="disabled"
                                                        fontSize="small"
                                                    />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </Box>
                        {/* Submit button moved here */}
                        <Box mt={2}>
                            <Tooltip
                                title={
                                    serviceIdList.every(
                                        (id) => completedServices[id],
                                    )
                                        ? 'Submit Pricing'
                                        : 'All services must be marked as done to submit pricing'
                                }
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmitAll}
                                    disabled={!allReviewed}
                                    fullWidth
                                >
                                    Submit Pricing
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                    {/* Main content area */}
                    <Box flex={1} p={3}>
                        <Typography fontWeight="bold" fontSize={20} mb={3}>
                            {selectedService &&
                                groupedById[selectedService] &&
                                groupedById[selectedService][0]?.service_name}
                        </Typography>
                        {/* Area (Partial Pricing) */}
                        <Box mb={5}>
                            <Typography fontWeight="bold" mb={1}>
                                Area (Partial Pricing)
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {editableAreaPricing.length === 0 ? (
                                <Typography color="text.secondary">
                                    No area pricing available.
                                </Typography>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {editableAreaPricing.map((item) => (
                                        <Box
                                            key={item.id}
                                            minWidth={220}
                                            mb={2}
                                        >
                                            <Typography>
                                                {item.area_config?.name}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                type="number"
                                                inputProps={{
                                                    step: '0.01',
                                                    min: '0',
                                                }}
                                                value={item.price ?? ''}
                                                margin="dense"
                                                onChange={(e) =>
                                                    handleAreaPriceChange(
                                                        item.id,
                                                        e.target.value,
                                                    )
                                                }
                                                error={
                                                    item.price !== null &&
                                                    item.price !== '' &&
                                                    !isValidDecimal(
                                                        String(item.price),
                                                    )
                                                }
                                                helperText={
                                                    item.price !== null &&
                                                    item.price !== '' &&
                                                    !isValidDecimal(
                                                        String(item.price),
                                                    )
                                                        ? 'Enter a valid decimal (e.g. 12.34)'
                                                        : ''
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        {/* Unit Types (Whole Unit Pricing) */}
                        <Box>
                            <Typography fontWeight="bold" mb={1}>
                                Unit Types (Whole Unit Pricing)
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {editableUnitPricing.length === 0 ? (
                                <Typography color="text.secondary">
                                    No unit pricing available.
                                </Typography>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {editableUnitPricing.map((item) => (
                                        <Box
                                            key={item.id}
                                            minWidth={220}
                                            mb={2}
                                        >
                                            <Typography>
                                                {item.unit_config?.name}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                type="number"
                                                inputProps={{
                                                    step: '0.01',
                                                    min: '0',
                                                }}
                                                value={item.price ?? ''}
                                                margin="dense"
                                                onChange={(e) =>
                                                    handleUnitPriceChange(
                                                        item.id,
                                                        e.target.value,
                                                    )
                                                }
                                                error={
                                                    item.price !== null &&
                                                    item.price !== '' &&
                                                    !isValidDecimal(
                                                        String(item.price),
                                                    )
                                                }
                                                helperText={
                                                    item.price !== null &&
                                                    item.price !== '' &&
                                                    !isValidDecimal(
                                                        String(item.price),
                                                    )
                                                        ? 'Enter a valid decimal (e.g. 12.34)'
                                                        : ''
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        {/* submit button */}
                        <Box display="flex" justifyContent="flex-start" mt={4}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleDoneService}
                                disabled={completedServices[selectedService!]}
                            >
                                {completedServices[selectedService!]
                                    ? 'Done'
                                    : 'Mark as Done'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Container>
    )
}
